import { getApiBase, getAuthHeaders } from '../config/api';
import { apiClient } from './apiClient';

// Interface cho dữ liệu thống kê
export interface StatisticsData {
  overview: {
    totalUsers: number;
    totalPosts: number;
    totalViews: number;
    totalRevenue: number;
    userGrowth: string;
    postGrowth: string;
    viewGrowth: string;
    revenueGrowth: string;
  };
  timeSeries: {
    users: Array<{ month: string; count: number }>;
    posts: Array<{ month: string; count: number }>;
    approvals: Array<{ month: string; approved: number; rejected: number }>;
  };
  categories: Array<{ name: string; count: number; percentage: number }>;
  packages: Array<{ name: string; count: number; percentage: number }>;
  topPosts: Array<{
    id: number;
    title: string;
    author: string;
    viewCount: number;
    createdAt: string;
  }>;
}

export class StatisticsService {
  private static baseUrl = getApiBase();

  static getAuthHeaders(): Record<string, string> {
    return getAuthHeaders();
  }

  /**
   * Lấy thống kê chi tiết cho dashboard
   */
  static async getDetailedStatistics(): Promise<StatisticsData> {
    try {
      const response = await apiClient.get('/admin/statistics/detailed');
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData?.message || 'Không thể tải thống kê chi tiết';
        throw new Error(Array.isArray(message) ? message.join(', ') : message);
      }
      
      return response.json();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Lấy thống kê tổng quan hệ thống
   */
  static async getSystemStatistics() {
    try {
      const response = await apiClient.get('/admin/statistics');
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData?.message || 'Không thể tải thống kê hệ thống';
        throw new Error(Array.isArray(message) ? message.join(', ') : message);
      }
      
      return response.json();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Lấy thống kê users theo role
   */
  static async getUsersByRole() {
    try {
      const response = await apiClient.get('/admin/statistics/users-by-role');
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData?.message || 'Không thể tải thống kê users theo role';
        throw new Error(Array.isArray(message) ? message.join(', ') : message);
      }
      
      return response.json();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Lấy thống kê giao dịch
   */
  static async getTransactionStatistics(fromDate?: string, toDate?: string) {
    try {
      const query = new URLSearchParams();
      if (fromDate) query.set('fromDate', fromDate);
      if (toDate) query.set('toDate', toDate);
      
      const response = await apiClient.get(`/admin/statistics/transactions${query.toString() ? `?${query.toString()}` : ''}`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData?.message || 'Không thể tải thống kê giao dịch';
        throw new Error(Array.isArray(message) ? message.join(', ') : message);
      }
      
      return response.json();
    } catch (error) {
      throw error;
    }
  }
}

// Export instance để tương thích với cách sử dụng cũ
export const statisticsService = {
  getDetailedStatistics: () => StatisticsService.getDetailedStatistics(),
  getSystemStatistics: () => StatisticsService.getSystemStatistics(),
  getUsersByRole: () => StatisticsService.getUsersByRole(),
  getTransactionStatistics: (fromDate?: string, toDate?: string) => 
    StatisticsService.getTransactionStatistics(fromDate, toDate),
};

export default StatisticsService;
